import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GlassCard } from "@/components/common/GlassCard";
import { AuthPageShell, AuthPageToggleFrame } from "@/components/layout/AuthPageShell";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/contexts/AuthContext";

export function AcceptOrgInvitationPage() {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();
  const { user, syncSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);

  useEffect(() => {
    if (!user || !invitationId || isAccepting) return;

    void (async () => {
      setIsAccepting(true);
      const { error: acceptError } = await authClient.organization.acceptInvitation({
        invitationId,
      });
      if (acceptError) {
        setError(acceptError.message ?? "Failed to accept invitation");
        setIsAccepting(false);
        return;
      }
      await syncSession();
      navigate("/", { replace: true });
    })();
  }, [user, invitationId, isAccepting, navigate, syncSession]);

  if (!invitationId) {
    return (
      <AuthPageToggleFrame>
        <p className="text-red-300">Invalid invitation link</p>
      </AuthPageToggleFrame>
    );
  }

  if (!user) {
    return (
      <AuthPageShell showBeta>
        <GlassCard className="border-indigo-300/25 bg-slate-950/45 p-7 shadow-[0_25px_80px_-45px_rgba(79,70,229,0.75)]">
          <div className="text-center">
            <h1 className="text-xl font-semibold text-white">Organization invitation</h1>
            <p className="mt-3 text-sm text-slate-300">Sign in to accept your invitation.</p>
            <Button variant="default" className="mt-6" asChild>
              <Link
                to={`/login?redirect=${encodeURIComponent(`/accept-invitation/${invitationId}`)}`}
              >
                Sign in
              </Link>
            </Button>
          </div>
        </GlassCard>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageToggleFrame>
      <p className="text-slate-300">{error ?? "Accepting invitation…"}</p>
    </AuthPageToggleFrame>
  );
}
